import React, { useState, useEffect } from 'react';
import prescriptionService from '../services/prescriptionService';

const EditPrescription = ({ prescription, onUpdateSuccess, onCancel }) => {
  // State for form data
  const [formData, setFormData] = useState({});
  // State for medications list
  const [medications, setMedications] = useState([]);
  // State for new medication form
  const [showNewMedForm, setShowNewMedForm] = useState(false);
  // State for new medication
  const [newMedication, setNewMedication] = useState({
    medicineId: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: ''
  });
  // State for loading and error handling
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // State for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // State for tracking changes
  const [originalData, setOriginalData] = useState({});
  const [changes, setChanges] = useState({});

  // Initialize form data when prescription changes
  useEffect(() => {
    if (prescription) {
      setFormData({...prescription});
      setOriginalData({...prescription});
      
      // Initialize medications list
      const initialMedications = Array.isArray(prescription.medications) 
        ? [...prescription.medications] 
        : [{
            medicineId: prescription.medicineId || '',
            dosage: prescription.dosage || '',
            frequency: prescription.frequency || '',
            duration: prescription.duration || '',
            instructions: prescription.instructions || ''
          }];
      
      setMedications(initialMedications);
    }
  }, [prescription]);

  // Handle form input changes
  const handleInputChange = (e, field) => {
    const newValue = e.target.value;
    console.log(`Updating ${field} to: ${newValue}`);
    
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  // Handle medication input change
  const handleMedicationChange = (e, index, field) => {
    const updatedMedications = [...medications];
    updatedMedications[index][field] = e.target.value;
    setMedications(updatedMedications);
  };

  // Handle new medication input change
  const handleNewMedicationChange = (e, field) => {
    setNewMedication({
      ...newMedication,
      [field]: e.target.value
    });
  };

  // Add new medication to the list
  const handleAddMedication = () => {
    // Validate required fields
    if (!newMedication.medicineId) {
      alert("Medicine ID is required");
      return;
    }
    
    setMedications([...medications, {...newMedication}]);
    setShowNewMedForm(false);
    setNewMedication({
      medicineId: '',
      dosage: '',
      frequency: '',
      duration: '',
      instructions: ''
    });
  };

  // Remove medication from the list
  const handleRemoveMedication = (index) => {
    const updatedMedications = [...medications];
    updatedMedications.splice(index, 1);
    setMedications(updatedMedications);
  };

  // Calculate changes and show confirmation modal
  const handleSaveChanges = () => {
    // Calculate changes for regular fields
    const newChanges = {};
    
    Object.keys(formData).forEach(key => {
      if (originalData[key] !== formData[key]) {
        newChanges[key] = {
          oldValue: originalData[key] || 'N/A',
          newValue: formData[key]
        };
      }
    });
    
    // Add medication changes
    if (!Array.isArray(prescription.medications) && medications.length > 1) {
      newChanges.medications = {
        oldValue: "1 medication",
        newValue: `${medications.length} medications`
      };
    } else if (Array.isArray(prescription.medications) && 
              prescription.medications.length !== medications.length) {
      newChanges.medications = {
        oldValue: `${prescription.medications.length} medications`,
        newValue: `${medications.length} medications`
      };
    }
    
    setChanges(newChanges);
    
    // Log formData to check if tests field is present
    console.log("Current form data:", formData);
    
    // Show modal if there are changes
    if (Object.keys(newChanges).length > 0 || 
        medications.length !== (Array.isArray(prescription.medications) ? 
                               prescription.medications.length : 1)) {
      setShowConfirmModal(true);
    } else {
      alert("No changes detected");
    }
  };

  // Submit changes to API
  const handleConfirmChanges = async () => {
    if (!prescription.id) {
      setError("No prescription ID found");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Create updated prescription with medications
      const updatedPrescription = {
        ...formData,
        medications: medications,
        // Explicitly include tests field to ensure it's sent to the API
        tests: formData.tests || ''
      };
      
      // If there's only one medication and no array existed before, also save at top level for backward compatibility
      if (medications.length === 1) {
        updatedPrescription.medicineId = medications[0].medicineId;
        updatedPrescription.dosage = medications[0].dosage;
        updatedPrescription.frequency = medications[0].frequency;
        updatedPrescription.duration = medications[0].duration;
        updatedPrescription.instructions = medications[0].instructions;
      }
      
      // Log the data being sent to the API
      console.log("Sending updated prescription:", updatedPrescription);
      console.log("Tests field value:", updatedPrescription.tests);
      
      // Call service to update prescription
      const result = await prescriptionService.updatePrescription(prescription.id, updatedPrescription);
      
      // Close the confirmation modal
      setShowConfirmModal(false);
      
      // Notify parent component of success and pass the updated data
      if (onUpdateSuccess) {
        onUpdateSuccess(result);
      }
      
      // Show success message
      alert("Prescription updated successfully");
      
      // Close the edit form
      if (onCancel) {
        onCancel();
      }
      
      // Reload the page to refresh the table data
      window.location.reload();
      
    } catch (err) {
      console.error("Error updating prescription:", err);
      setError(err.message || "Failed to update prescription. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Cancel editing and go back
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Render the confirmation modal
  const renderConfirmationModal = () => {
    if (!showConfirmModal) return null;
    
    return (
      <div className="modal show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">Confirm Changes</h5>
              <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)}></button>
            </div>
            <div className="modal-body">
              <h4>Summary of Changes</h4>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-info">
                    <tr>
                      <th>Field</th>
                      <th>Original Value</th>
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(changes).length > 0 ? (
                      Object.keys(changes).map(key => (
                        <tr key={key}>
                          <td>{key.charAt(0).toUpperCase() + key.slice(1)}</td>
                          <td>{changes[key].oldValue}</td>
                          <td className="text-success">{changes[key].newValue}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">No changes made</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Medications changes summary */}
              {(medications.length !== (Array.isArray(prescription.medications) ? 
                                        prescription.medications.length : 1)) && (
                <div className="mt-3">
                  <h5>Medication Changes</h5>
                  <div className="alert alert-info">
                    {Array.isArray(prescription.medications) ? 
                      `Changed from ${prescription.medications.length} to ${medications.length} medications` : 
                      `Changed from 1 to ${medications.length} medications`}
                  </div>
                </div>
              )}
              
              {/* Display tests field changes if modified */}
              {changes.tests && (
                <div className="mt-3">
                  <h5>Tests Changes</h5>
                  <div className="alert alert-info">
                    <strong>Original:</strong> {changes.tests.oldValue || 'None'}<br/>
                    <strong>New:</strong> <span className="text-success">{changes.tests.newValue}</span>
                  </div>
                </div>
              )}
              
              {/* Show error message if any */}
              {error && (
                <div className="alert alert-danger mt-3">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowConfirmModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-success" 
                onClick={handleConfirmChanges}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>Confirm Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render medications table
  const renderMedicationsTable = () => {
    return (
      <>
        <table className="table table-striped text-center">
          <thead className="table-info">
            <tr>
              <th>Medicine ID</th>
              <th>Dosage</th>
              <th>Frequency</th>
              <th>Duration</th>
              <th>Instructions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {medications.length > 0 ? (
              medications.map((med, index) => (
                <tr key={index}>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={med.medicineId || ''} 
                      onChange={(e) => handleMedicationChange(e, index, 'medicineId')} 
                      required
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={med.dosage || ''} 
                      onChange={(e) => handleMedicationChange(e, index, 'dosage')} 
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={med.frequency || ''} 
                      onChange={(e) => handleMedicationChange(e, index, 'frequency')} 
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={med.duration || ''} 
                      onChange={(e) => handleMedicationChange(e, index, 'duration')} 
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={med.instructions || ''} 
                      onChange={(e) => handleMedicationChange(e, index, 'instructions')} 
                    />
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveMedication(index)}
                      disabled={medications.length === 1}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">No medications</td>
              </tr>
            )}
          </tbody>
        </table>
        
        {/* Add new medication form */}
        {showNewMedForm && (
          <div className="card mb-3 mt-2">
            <div className="card-header bg-light">
              <h5>New Medication</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md">
                  <label className="form-label">Medicine ID <span className="text-danger">*</span></label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newMedication.medicineId} 
                    onChange={(e) => handleNewMedicationChange(e, 'medicineId')}
                    required
                  />
                </div>
                <div className="col-md">
                  <label className="form-label">Dosage</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newMedication.dosage} 
                    onChange={(e) => handleNewMedicationChange(e, 'dosage')}
                  />
                </div>
                <div className="col-md">
                  <label className="form-label">Frequency</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newMedication.frequency} 
                    onChange={(e) => handleNewMedicationChange(e, 'frequency')}
                  />
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md">
                  <label className="form-label">Duration</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newMedication.duration} 
                    onChange={(e) => handleNewMedicationChange(e, 'duration')}
                  />
                </div>
                <div className="col-md">
                  <label className="form-label">Instructions</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newMedication.instructions} 
                    onChange={(e) => handleNewMedicationChange(e, 'instructions')}
                  />
                </div>
              </div>
              <div className="mt-3">
                <button 
                  className="btn btn-success me-2" 
                  onClick={handleAddMedication}
                >
                  <i className="bi bi-check-lg me-1"></i> Add
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowNewMedForm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add medication button */}
        {!showNewMedForm && (
          <button 
            className="btn btn-outline-primary btn-sm mt-2"
            onClick={() => setShowNewMedForm(true)}
          >
            <i className="bi bi-plus-circle me-1"></i> Add Medication
          </button>
        )}
      </>
    );
  };

  if (!prescription) {
    return <div className="alert alert-warning">No prescription selected for editing</div>;
  }

  return (
    <div className="container-fluid p-0">
      {renderConfirmationModal()}
      
      <div className="row align-items-center mb-3">
        <div className="col">
          <h3>Edit Prescription</h3>
        </div>
        <div className="col-auto">
          <button 
            className="btn btn-success me-2" 
            onClick={handleSaveChanges}
            disabled={isSubmitting}
          >
            <i className="bi bi-check-lg me-1"></i> Save Changes
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Prescription ID</label>
          <input 
            type="text" 
            className="form-control" 
            value={prescription.id || 'N/A'} 
            readOnly 
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Date Prescribed</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.datePrescribed || ''} 
            onChange={(e) => handleInputChange(e, 'datePrescribed')}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Doctor ID</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.doctorId || ''} 
            onChange={(e) => handleInputChange(e, 'doctorId')}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Doctor Name</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.doctorName || ''} 
            onChange={(e) => handleInputChange(e, 'doctorName')}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Patient ID</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.patientId || ''} 
            onChange={(e) => handleInputChange(e, 'patientId')}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Patient Name</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.patientName || ''} 
            onChange={(e) => handleInputChange(e, 'patientName')}
          />
        </div>
      </div>

      <div className="mb-3">
        <h4>Clinical Information</h4>
        <div className="row">
          <div className="col-md-12 mb-3">
            <label className="form-label fw-bold">Symptoms</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.symptoms || ''}
              onChange={(e) => handleInputChange(e, 'symptoms')}
            ></textarea>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 mb-3">
            <label className="form-label fw-bold">Diseases</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.diseases || ''}
              onChange={(e) => handleInputChange(e, 'diseases')}
            ></textarea>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <h4>Medications</h4>
        <div className="table-responsive">
          {renderMedicationsTable()}
        </div>
      </div>

      <div className="mb-3">
        <h4>Tests</h4>
        <textarea
          className="form-control"
          rows="3"
          value={formData.tests || ''}
          onChange={(e) => handleInputChange(e, 'tests')}
          id="testsTextarea" // Added ID for easier debugging
        ></textarea>
        {/* Debug info to verify tests value is being captured */}
        <small className="text-muted">Current value: {formData.tests}</small>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Refills</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.refills || ''} 
            onChange={(e) => handleInputChange(e, 'refills')}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold">Consultation Type</label>
          <input 
            type="text" 
            className="form-control" 
            value={formData.consultationType || ''} 
            onChange={(e) => handleInputChange(e, 'consultationType')}
          />
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}
      
      <div className="d-flex justify-content-end mt-4 mb-3">
        <button 
          className="btn btn-secondary me-2"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          <i className="bi bi-x-lg me-1"></i> Cancel
        </button>
        <button 
          className="btn btn-success"
          onClick={handleSaveChanges}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <><i className="bi bi-check-lg me-1"></i> Save Changes</>
          )}
        </button>
      </div>
    </div>
  );
};

export default EditPrescription;